import { FieldSubject, IncomeOption } from '@hb/domain';
import { money } from '@hb/builder';
import { ApplicantsStoreService } from '@hb/modules/applicants/applicants-store.service';
import { AbstractStoreInjectOption } from '@hb/services/store/abstract-store-inject.service';

import { inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, skip, Subject } from 'rxjs';

import { Money } from '@ap/interface-bspc-core';
import { Applicants } from '@ap/ap-interface-apix-hypotheken-berekening';

@Injectable({
    providedIn: 'root'
})
export class IncomeInputStoreService extends AbstractStoreInjectOption<IncomeOption> implements OnDestroy {
    protected readonly store = inject(ApplicantsStoreService);
    protected readonly lastQuestion = IncomeOption.SUMMARY;

    private readonly mainApplicantIncomeSubject: FieldSubject<Money>;
    private readonly coApplicantIncomeSubject: BehaviorSubject<Money>;
    private readonly emitFormValidation: Subject<void> = new Subject();

    constructor() {
        super();
        this.init();
        const initialValues = this.getInitialValues();
        this.mainApplicantIncomeSubject = new FieldSubject<Money>(initialValues?.main_applicant?.income);
        this.coApplicantIncomeSubject = new BehaviorSubject<Money>(initialValues?.co_applicant?.income);

        this.subscriptions();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.activeQuestionSubject.unsubscribe();
        this.mainApplicantIncomeSubject.unsubscribe();
        this.coApplicantIncomeSubject.unsubscribe();
    }

    update(): void {
        this.store.setMainApplicantIncome(this.mainApplicantIncomeSubject.value);
        this.store.setCoApplicantIncome(this.coApplicantIncomeSubject.value);
    }

    isActiveQuestion(question: IncomeOption): boolean {
        switch (question) {
            case IncomeOption.INCOME_INPUT:
                return true;
            case IncomeOption.SUMMARY:
            default:
                return false;
        }
    }

    nextQuestion(next = 1) {
        super.nextQuestion(next);
        this.setDisplayFullAccordion();
    }

    getMainApplicantIncome(): Money {
        return this.mainApplicantIncomeSubject.value;
    }

    setMainApplicantIncome(amount: number): void {
        this.mainApplicantIncomeSubject.next(money(amount));
    }

    getCoApplicantIncome(): Money {
        return this.coApplicantIncomeSubject.value;
    }

    setCoApplicantIncome(amount: number): void {
        this.coApplicantIncomeSubject.next(money(amount));
    }

    getInitialValues(): Applicants {
        return this.store.getApplicantsState();
    }

    private subscriptions(): void {
        this.mainApplicantIncomeSubject.asObservable().subscribe(() => this.update());
        this.coApplicantIncomeSubject
            .asObservable()
            .pipe(skip(1))
            .subscribe(() => this.update());
    }

    setDisplayFullAccordion() {
        this.store.setDisplayFullAccordion();
    }

    triggerFormFieldValidation() {
        this.emitFormValidation.next();
    }

    getFormFieldValidationObservable(): Observable<void> {
        return this.emitFormValidation.asObservable();
    }
}
